import React, { useEffect, Suspense } from 'react';
import { Route, Switch, withRouter, Redirect } from 'react-router-dom';

// CUSTOM COMPONENT
import LoadingSpinner from './shared/components/UIElements/LoadingSpinner/LoadingSpinner';
import MainAdminNavigation from './shared/components/Navigation/MainAdminNavigation/MainAdminNavigation';
import MainNavigation from './shared/components/Navigation/MainNavigation/MainNavigation';

// CUSTOM CONTEXT & HOOK
import { AuthContext } from './shared/context/auth-context';
import { LanguageContext } from './shared/context/language-context';
import { PostContext } from './shared/context/post-context';
import { ProductContext } from './shared/context/product-context';
import { CategoryContext } from './shared/context/category-context';
import { SectionContext } from './shared/context/section-context';
import { useAuth } from './shared/hooks/auth-hook';
import { useLanguage } from './shared/hooks/language-hook';
import { usePost } from './shared/hooks/post-hook';
import { useProduct } from './shared/hooks/product-hook';
import { useCategory } from './shared/hooks/category-hook';
import { useSection } from './shared/hooks/section-hook';

// ADMIN SIDE COMPONENT
const AdminSettingsPage = React.lazy(() =>
  import('./adminSettingsPage/pages/AdminSettingsPage')
);
const AdminSinglePostPage = React.lazy(() =>
  import('./adminPostsPage/pages/AdminSinglePostPage')
);
const AdminPostEditPage = React.lazy(() =>
  import('./adminPostsPage/pages/AdminPostEditPage')
);
const AdminPostCreatePage = React.lazy(() =>
  import('./adminPostsPage/pages/AdminPostCreatePage')
);
const AdminPostsPage = React.lazy(() =>
  import('./adminPostsPage/pages/AdminPostsPage')
);
const AdminSingleProductPage = React.lazy(() =>
  import('./adminProductsPage/pages/AdminSingleProductPage')
);
const AdminProductEditPage = React.lazy(() =>
  import('./adminProductsPage/pages/AdminProductEditPage')
);
const AdminProductCreatePage = React.lazy(() =>
  import('./adminProductsPage/pages/AdminProductCreatePage')
);
const AdminProductsPage = React.lazy(() =>
  import('./adminProductsPage/pages/AdminProductsPage')
);
const AdminHomePage = React.lazy(() =>
  import('./adminHomePage/pages/AdminHomePage')
);

// USER SIDE COMPONENT
const NewPasswordPage = React.lazy(() =>
  import('./auth/pages/NewPasswordPage')
);
const ResetPage = React.lazy(() => import('./auth/pages/ResetPage'));
const AuthPage = React.lazy(() => import('./auth/pages/AuthPage'));
const CareersPage = React.lazy(() => import('./careersPage/pages/CareersPage'));
const SingleNewsPage = React.lazy(() =>
  import('./newsPage/pages/SingleNewsPage')
);
const NewsPage = React.lazy(() => import('./newsPage/pages/NewsPage'));
const AboutPage = React.lazy(() => import('./aboutPage/pages/AboutPage'));
const SanRafaelPage = React.lazy(() =>
  import('./storesPage/pages/SanRafaelPage')
);
const EmeryvillePage = React.lazy(() =>
  import('./storesPage/pages/EmeryvillePage')
);
const StoresPage = React.lazy(() => import('./storesPage/pages/StoresPage'));
const SingleProductPage = React.lazy(() =>
  import('./productsPage/pages/SingleProductPage')
);
const ProductsPage = React.lazy(() =>
  import('./productsPage/pages/ProductsPage')
);
const HomePage = React.lazy(() => import('./homePage/pages/HomePage'));

const App = (props) => {
  const { token, login, logout, admin, update } = useAuth();
  const { language, changeLanguageHandler } = useLanguage();
  const { posts, totalPosts, fetchAllPosts } = usePost();
  const { products, totalProducts, fetchAllProducts } = useProduct();
  const { categories, fetchAllCategories } = useCategory();
  const { sections, fetchAllSections } = useSection();

  useEffect(() => {
    fetchAllPosts();
    fetchAllProducts();
    fetchAllCategories();
    fetchAllSections();
  }, [fetchAllPosts, fetchAllProducts, fetchAllCategories, fetchAllSections]);

  let routes;

  if (token) {
    routes = (
      <Switch>
        <Route path="/settings" component={AdminSettingsPage} />
        <Route path="/posts/view/:postId" component={AdminSinglePostPage} />
        <Route path="/posts/edit/:postId" component={AdminPostEditPage} />
        <Route path="/posts/create" component={AdminPostCreatePage} />
        <Route path="/posts" component={AdminPostsPage} />
        <Route
          path="/products/view/:productId"
          exact
          component={AdminSingleProductPage}
        />
        <Route
          path="/products/edit/:productId"
          exact
          component={AdminProductEditPage}
        />
        <Route
          path="/products/create"
          exact
          component={AdminProductCreatePage}
        />
        <Route path="/products" exact component={AdminProductsPage} />
        <Route path="/" component={AdminHomePage} />
        <Redirect to="/" />
      </Switch>
    );
  } else {
    routes = (
      <Switch>
        <Route path="/reset/:resetToken" component={NewPasswordPage} />
        <Route path="/reset" component={ResetPage} />
        <Route path="/auth" component={AuthPage} />
        <Route path="/careers" component={CareersPage} />
        <Route path="/news/:postId" component={SingleNewsPage} />
        <Route path="/news" component={NewsPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/stores/store-2" component={SanRafaelPage} />
        <Route path="/stores/store-1" component={EmeryvillePage} />
        <Route path="/stores" component={StoresPage} />
        <Route path="/products/:productId" component={SingleProductPage} />
        <Route path="/products" component={ProductsPage} />
        <Route path="/" exact component={HomePage} />
        <Redirect to="/" />
      </Switch>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        admin: admin,
        token: token,
        login: login,
        logout: logout,
        update: update,
      }}
    >
      <LanguageContext.Provider
        value={{
          language: language,
          change: changeLanguageHandler,
        }}
      >
        <PostContext.Provider
          value={{
            posts: posts,
            totalPosts: totalPosts,
            fetchAllPosts: fetchAllPosts,
          }}
        >
          <ProductContext.Provider
            value={{
              products: products,
              totalProducts: totalProducts,
              fetchAllProducts: fetchAllProducts,
            }}
          >
            <CategoryContext.Provider
              value={{
                categories: categories,
                fetchAllCategories: fetchAllCategories,
              }}
            >
              <SectionContext.Provider
                value={{
                  sections: sections,
                  fetchAllSections: fetchAllSections,
                }}
              >
                {token ? (
                  <MainAdminNavigation>
                    <Suspense fallback={<LoadingSpinner asOverlay />}>
                      {routes}
                    </Suspense>
                  </MainAdminNavigation>
                ) : (
                  <MainNavigation {...props}>
                    <Suspense fallback={<LoadingSpinner asOverlay />}>
                      {routes}
                    </Suspense>
                  </MainNavigation>
                )}
              </SectionContext.Provider>
            </CategoryContext.Provider>
          </ProductContext.Provider>
        </PostContext.Provider>
      </LanguageContext.Provider>
    </AuthContext.Provider>
  );
};

export default withRouter(App);
